import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Compass, Ruler, ShieldCheck } from "lucide-react";

const para = "font-inter text-base lg:text-lg text-brand-mute leading-relaxed";

export default function About() {
  return (
    <section
      id="nosotros"
      data-testid="about-section"
      className="relative bg-white py-24 lg:py-32 overflow-hidden"
    >
      {/* Left navy accent bar */}
      <div className="hidden lg:block absolute left-0 top-1/4 h-1/2 w-1 bg-brand-navy/10" />
      <div className="hidden lg:block absolute left-0 top-1/3 h-1/3 w-1 bg-brand-gold" />

      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
        {/* LEFT — Visual */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[4/5] max-w-[440px] mx-auto">
            {/* Background navy card with gold border */}
            <div className="absolute -top-6 -left-6 w-full h-full bg-brand-navy rounded-sm border border-brand-gold/40">
              <div aria-hidden className="absolute inset-0 bg-blueprint-dark bg-grid-32 opacity-60 rounded-sm" />
            </div>

            {/* Front white card */}
            <div className="relative h-full w-full bg-brand-light border border-brand-navy/10 rounded-sm shadow-[0_30px_60px_-30px_rgba(27,42,74,0.3)] flex flex-col items-center justify-center p-10 overflow-hidden">
              <div aria-hidden className="absolute inset-0 bg-dots-grid bg-dots-24 opacity-70" />

              {/* Compass illustration */}
              <svg viewBox="0 0 220 220" className="relative w-40 h-40 text-brand-gold" fill="none" stroke="currentColor" strokeWidth="1.4">
                <circle cx="110" cy="110" r="92" />
                <circle cx="110" cy="110" r="68" strokeDasharray="2 5" />
                <circle cx="110" cy="110" r="3" fill="currentColor" />
                <line x1="110" y1="18" x2="110" y2="202" />
                <line x1="18" y1="110" x2="202" y2="110" />
                <polyline points="110,38 122,110 110,182 98,110 110,38" stroke="#1B2A4A" strokeWidth="1.6" />
                <line x1="60" y1="60" x2="160" y2="160" strokeWidth="0.8" />
                <line x1="160" y1="60" x2="60" y2="160" strokeWidth="0.8" />
              </svg>

              <div className="relative mt-6 text-center">
                <div className="font-mont text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                  Despacho fundado en
                </div>
                <div className="font-playfair text-5xl text-brand-navy mt-1">1982</div>
              </div>

              {/* Gold pill */}
              <span className="absolute -bottom-3 right-6 inline-flex items-center gap-2 bg-brand-gold text-brand-navy font-mont text-[10px] uppercase tracking-[0.22em] px-4 py-2 rounded-sm shadow-md">
                <Ruler size={12} /> Precisión Técnica
              </span>
            </div>
          </div>
        </motion.div>

        {/* RIGHT — Content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="lg:col-span-7"
        >
          <div className="flex items-center gap-3">
            <span className="h-px w-10 bg-brand-gold" />
            <span className="label-overline">Sobre Nosotros</span>
          </div>

          <h2 className="mt-5 font-playfair text-3xl sm:text-4xl lg:text-5xl text-brand-navy leading-[1.1] tracking-tight text-balance">
            Arq. Angel Rodríguez Juárez —{" "}
            <span className="italic text-brand-gold">una trayectoria</span> construida en confianza
          </h2>

          <div className="mt-7 space-y-5 max-w-2xl">
            <p className={para}>
              Arquitecto titulado con más de cuatro décadas de práctica profesional en Puebla,
              especializado en valuación inmobiliaria, peritaje técnico y Dirección Responsable
              de Obra. Su despacho ha acompañado a propietarios, promotores, autoridades y
              particulares en miles de procesos donde la exactitud técnica y la validez legal
              no son negociables.
            </p>
            <p className={para}>
              Cuenta con formación académica continua, registro vigente como valuador y como
              Director Responsable de Obra ante el municipio de Puebla, además de ser perito
              reconocido por instancias judiciales y dependencias oficiales. Mantiene afiliación
              activa a colegios y asociaciones profesionales del gremio.
            </p>
            <p className={para}>
              Trabajar con nosotros significa atención personal, tiempos de respuesta competitivos,
              metodología rigurosa y documentos con plena validez para trámites notariales,
              fiscales y judiciales.
            </p>
          </div>

          {/* Credential pills */}
          <div className="mt-8 flex flex-wrap gap-3">
            {[
              { icon: Compass,     label: "Arquitecto" },
              { icon: Ruler,       label: "Valuador Certificado" },
              { icon: ShieldCheck, label: "DRO Autorizado" },
            ].map(({ icon: Icon, label }) => (
              <span
                key={label}
                className="inline-flex items-center gap-2 bg-brand-navy text-brand-gold font-mont text-[10px] uppercase tracking-[0.22em] px-4 py-2.5 rounded-sm"
              >
                <Icon size={14} strokeWidth={1.8} />
                {label}
              </span>
            ))}
          </div>

          <a
            href="#servicios"
            data-testid="about-cta-services"
            className="mt-9 inline-flex items-center gap-2 font-mont text-xs font-semibold uppercase tracking-[0.18em] text-brand-gold-dark group"
          >
            Conoce nuestros servicios
            <ArrowRight size={16} className="transition-transform group-hover:translate-x-1.5" />
            <span className="block h-px bg-brand-gold w-12 group-hover:w-20 transition-all" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
